import {
  RBPayBusinessType,
  RBPayBusinessTypeValue,
  RBPayEmploymentType,
  RBPayEmploymentTypeValue,
  RBPayPaymentMethod,
  RBPayPaymentMethodValue,
  RBPayTaxProfile,
  RBPayTaxProfileValue,
} from "../types/rbPaySettings.types";

export const normalizeDigits = (value: string = "") => String(value || "").replace(/\D/g, "");

export const normalizeBusinessNumber = (value: string = "") =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

export const formatSin = (value: string = "") => {
  const digits = normalizeDigits(value).slice(0, 9);

  if (!digits) {
    return "";
  }

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Date of Birth is an opaque calendar date, never a timestamp: the backend stores
// it in the `date` column pharmacists.date_of_birth and returns "YYYY-MM-DD".
// Truncating the string handles both shapes the backend can emit ("1990-05-04" and
// "1990-05-04T00:00:00.000Z") WITHOUT going through a local `Date` — in a negative
// UTC-offset timezone (all of Canada) parsing the latter and formatting it locally
// shifts the day back by one, which is the classic date-of-birth bug.
export const normalizeDateOfBirth = (value: string = "") => {
  const matchedDate = String(value || "").trim().match(/^\d{4}-\d{2}-\d{2}/);
  return matchedDate ? matchedDate[0] : "";
};

export const maskSin = (value: string = "") => {
  const digits = normalizeDigits(value);

  if (!digits) {
    return "";
  }

  const lastThreeDigits = digits.slice(-3);
  return `***-***-${lastThreeDigits}`;
};

export const formatBusinessNumber = (value: string = "") => {
  const rawValue = normalizeBusinessNumber(value).slice(0, 15);
  const rootDigits = rawValue.slice(0, 9);
  const suffix = rawValue.slice(9);

  if (!rootDigits) {
    return "";
  }

  const formattedRoot = rootDigits.replace(/(\d{3})(?=\d)/g, "$1-");
  return suffix ? `${formattedRoot} ${suffix}` : formattedRoot;
};

// ---------------------------------------------------------------------------
// Business Number: ONE value, ONE field — "123-456-789 RT0001". The RT/RC program
// account code is fixed by business type (Sole Proprietor -> RT, Incorporated ->
// RC); only the 9-digit root and the 4-digit suffix are user-editable. Mirrors
// RB-Portal (src/util/workerRbPayroll.js) exactly.
// ---------------------------------------------------------------------------
export const getBusinessNumberPrefixForType = (businessType: RBPayBusinessTypeValue) =>
  businessType === RBPayBusinessType.INCORPORATED ? "RC" : "RT";

// Keystroke mask (digits only, RT/RC locked by type, auto dashes + space). No
// default suffix while typing — mirrors the `999-999-999 RT9999` input mask.
// `previousValue` lets a backspace over a fixed mask char (a dash, the space, or
// the RT/RC prefix) delete the preceding digit instead — the react-input-mask
// pattern — so the user is never stuck on the locked prefix when clearing.
export const maskBusinessNumber = (
  value: string = "",
  businessType: RBPayBusinessTypeValue,
  previousValue: string = ""
) => {
  let digits = normalizeDigits(value);
  const previousDigits = normalizeDigits(previousValue);

  if (
    digits.length > 0 &&
    digits === previousDigits &&
    String(value).length < String(previousValue).length
  ) {
    digits = digits.slice(0, -1);
  }

  const root = digits.slice(0, 9);
  const suffix = digits.slice(9, 13);
  const formattedRoot = root.replace(/(\d{3})(?=\d)/g, "$1-");

  if (root.length < 9) {
    return formattedRoot;
  }

  return `${formattedRoot} ${getBusinessNumberPrefixForType(businessType)}${suffix}`;
};

// Canonical value for seeding / business-type swaps: like maskBusinessNumber but
// defaults the 4-digit suffix to 0001 once the root is complete. "" for empty root.
export const composeBusinessNumber = (value: string = "", businessType: RBPayBusinessTypeValue) => {
  const digits = normalizeDigits(value);
  const root = digits.slice(0, 9);

  if (root.length < 9) {
    return formatBusinessNumber(root);
  }

  const suffix = digits.slice(9, 13) || "0001";
  return formatBusinessNumber(root + getBusinessNumberPrefixForType(businessType) + suffix);
};

// Pre-filled default for a fresh business profile: "000-000-000 RT0001" / "...RC0001".
// A real field value, not a placeholder.
export const defaultBusinessNumber = (businessType: RBPayBusinessTypeValue) =>
  formatBusinessNumber(`000000000${getBusinessNumberPrefixForType(businessType)}0001`);

// Infer business type from an existing business number's RT/RC program-account
// code (RC -> Incorporated, otherwise Sole Proprietor). The number is the source
// of truth for the type — mirrors RB-Portal's businessTypeFromValue.
export const businessTypeFromValue = (value: string = ""): RBPayBusinessTypeValue =>
  normalizeBusinessNumber(value).slice(9, 11) === "RC"
    ? RBPayBusinessType.INCORPORATED
    : RBPayBusinessType.SOLE_PROPRIETOR;

export const maskAccountNumber = (value: string = "") => {
  const digits = normalizeDigits(value);

  if (!digits) {
    return "";
  }

  const lastFourDigits = digits.slice(-4);
  const hiddenCount = Math.max(5, digits.length - 4);
  return `${"*".repeat(hiddenCount)}${lastFourDigits}`;
};

export const getEmploymentTypeLabel = (value: RBPayEmploymentTypeValue | null) => {
  if (value === RBPayEmploymentType.BUSINESS) {
    return "Business";
  }

  if (value === RBPayEmploymentType.EMPLOYEE) {
    return "Employee";
  }

  return "N/A";
};

export const getPaymentMethodLabel = (value: RBPayPaymentMethodValue) => {
  if (value === RBPayPaymentMethod.EMAIL_TRANSFER) {
    return "Email Money Transfer";
  }

  return "Direct Deposit";
};

export const getTaxProfileLabel = (value: RBPayTaxProfileValue) => {
  if (value === RBPayTaxProfile.LOW) {
    return "Low Tax Profile";
  }

  return "High Tax Profile";
};

export const getBusinessTypeLabel = (value: RBPayBusinessTypeValue) => {
  if (value === RBPayBusinessType.INCORPORATED) {
    return "Incorporated";
  }

  if (value === RBPayBusinessType.SOLE_PROPRIETOR) {
    return "Sole Proprietor";
  }

  return "N/A";
};

export const isImageDocument = (mimeType: string = "") => mimeType.startsWith("image/");
